/**
 * The portal's API client.
 *
 * Two things here are load-bearing and easy to get wrong.
 *
 * 1. **The CSRF token is bound to the session id, and the session id changes
 *    on sign-in and registration.** A token fetched before either stops
 *    working immediately afterwards, and the symptom is a 403 on the very
 *    first thing an owner does. So the token is cached, cleared whenever
 *    privilege changes, and re-fetched on demand.
 *
 * 2. **A 403 can mean a stale token rather than a real refusal.** One retry
 *    with a fresh token turns a confusing failure into nothing at all — but
 *    only one, or a genuinely forbidden action becomes an infinite loop.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message?: string,
  ) {
    super(message ?? code)
    this.name = 'ApiError'
  }
}

let csrfToken: string | null = null

/**
 * What to do when the server says the session is gone.
 *
 * A session outlives a browser tab, so an owner can leave the portal open
 * overnight and come back to one that expired. Without this every screen
 * catches its own 401 and renders "Something went wrong" — which is both
 * wrong and unactionable, because the thing to do is sign in again. The shell
 * registers a handler and flips itself to signed-out instead.
 */
let onSessionLost: (() => void) | null = null

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler
}

async function fetchCsrfToken(): Promise<string> {
  const res = await fetch('/api/auth/csrf', { credentials: 'same-origin' })
  if (!res.ok) throw new ApiError(res.status, 'csrf_unavailable')
  const body = (await res.json()) as { token: string }
  csrfToken = body.token
  return body.token
}

/** Called after anything that changes who you are. */
export function forgetCsrfToken(): void {
  csrfToken = null
}

async function request<T>(method: string, path: string, body?: unknown, isRetry = false): Promise<T> {
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (method !== 'GET') headers['x-csrf-token'] = csrfToken ?? (await fetchCsrfToken())

  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })

  if (res.status === 403 && !isRetry && method !== 'GET') {
    // Probably a token that went stale when the session id changed. Try once
    // with a fresh one; a genuine refusal will 403 again and stop here.
    forgetCsrfToken()
    return request<T>(method, path, body, true)
  }

  // A 401 from anywhere but the session probe means the session went away
  // under us. `/api/auth/me` is excluded because a 401 there is the ordinary
  // answer for a visitor who has never signed in, and treating it as a loss
  // would fire the handler on every first page load.
  if (res.status === 401 && path !== '/api/auth/me') {
    forgetCsrfToken()
    onSessionLost?.()
  }

  if (res.status === 204) return undefined as T

  const parsed = (await res.json().catch(() => ({}))) as { error?: string; message?: string }
  if (!res.ok) throw new ApiError(res.status, parsed.error ?? 'request_failed', parsed.message)
  return parsed as T
}

// --- Types mirroring what the service returns ---

export type SubscriptionStatus = 'unpaid' | 'active' | 'lapsed' | 'trial'

export interface Subscription {
  status: SubscriptionStatus
  plan: string
  paidThrough: string | null
  trialEndsAt: string | null
  priceCents: number
  deviceAllowance: number
  entitlements: string[]
  daysRemaining: number | null
  trialDaysRemaining: number | null
  daysLapsed: number | null
  capabilities: {
    canDownloadInstaller: boolean
    canEnrolDevice: boolean
    canReleaseDevice: boolean
    canSync: boolean
  }
}

export interface Device {
  deviceId: string
  active: boolean
  enrolledAt: string
  releasedAt: string | null
  lastLicenceCheckAt: string | null
  lastSeenVersion: string | null
}

export interface EnrolmentCode {
  /** Shown once. Not stored anywhere, here or on the server. */
  code: string
  issuedAt: string
  expiresAt: string
}

export interface LiveEnrolmentCode {
  issuedAt: string
  expiresAt: string
  secondsRemaining: number
}

export interface DevicesView {
  devices: Device[]
  allowance: { limit: number; used: number; canEnrolAnother: boolean }
}

export interface Payment {
  reference: string
  status: 'pending' | 'succeeded' | 'failed'
  source: 'paystack' | 'operator'
  amountCents: number
  currency: string
  paidAt: string | null
  periodEnd: string | null
  createdAt: string
}

export interface Session {
  accountId: string
  tenantId: string
}

// --- Endpoints ---

export const api = {
  session: () => request<Session>('GET', '/api/auth/me'),
  register: (body: { email: string; password: string; businessName: string }) =>
    request<{ tenantId: string; businessName: string }>('POST', '/api/auth/register', body),
  signIn: (body: { email: string; password: string }) =>
    request<{ tenantId: string }>('POST', '/api/auth/login', body),
  signOut: () => request<void>('POST', '/api/auth/logout'),
  subscription: () => request<Subscription>('GET', '/api/subscription'),
  devices: () => request<DevicesView>('GET', '/api/devices'),
  releaseDevice: (deviceId: string) =>
    request<void>('POST', `/api/devices/${deviceId}/release`),
  /**
   * M1-12a. The response is the only time the code is ever served — nothing
   * stores it, so nothing can hand it back.
   */
  createEnrolmentCode: () =>
    request<EnrolmentCode>('POST', '/api/devices/enrolment-code'),
  /** Whether one is still live, and for how long. Never the code itself. */
  liveEnrolmentCode: () =>
    request<{ code: LiveEnrolmentCode | null }>('GET', '/api/devices/enrolment-code'),
  payments: () => request<{ payments: Payment[] }>('GET', '/api/billing/payments'),
  startCheckout: () =>
    request<{ authorizationUrl: string; reference: string; amountCents: number }>(
      'POST',
      '/api/billing/checkout',
      { callbackPath: '/billing/return' },
    ),
  business: () => request<Business>('GET', '/api/business'),
  /** M1-24. Partial: a form that touched one field sends one field. */
  updateBusiness: (body: BusinessEdit) => request<Business>('PATCH', '/api/business', body),
  currentRelease: () => request<ReleaseView>('GET', '/api/releases/current'),
  backup: () => request<BackupState>('GET', '/api/backup'),
}

/**
 * `null` clears a field, `undefined` leaves it alone.
 *
 * The distinction is the whole reason this is a separate type from `Business`:
 * an owner who blanks the paybill box means "remove it", and a client that
 * could not say so would make a filled field impossible to empty.
 */
export interface BusinessEdit {
  name?: string
  receipt?: Partial<Record<keyof Business['receipt'], string | null>>
}

/** M1-28 — backup state (§7.21). */
export interface BackupDevice {
  deviceId: string
  active: boolean
  enrolledAt: string
  releasedAt: string | null
  /** Ours: when a batch from this machine last committed. */
  lastSyncAt: string | null
  lastSyncAgeHours: number | null
  /** The device's own word about its outbox — never something we measured. */
  reportedQueueDepth: number | null
  reportedOldestUnsyncedAt: string | null
  reportedOldestUnsyncedAgeHours: number | null
  records: number
  health: 'never' | 'current' | 'stale' | 'overdue' | 'released'
}

export interface BackupState {
  devices: BackupDevice[]
  totalRecords: number
  lastSyncAt: string | null
  attentionNeeded: boolean
  reportedFieldsAreDeviceClaims: boolean
}

export interface Business {
  name: string
  email: string | null
  locale: string
  timezone: string
  currency: string
  receipt: {
    businessName: string | null
    locationLine: string | null
    paybill: string | null
    accountNumber: string | null
    phone: string | null
    footer: string | null
  }
}

export interface ReleaseView {
  release: {
    version: string
    sizeBytes: number
    sha256: string
    notes: string
    releasedAt: string | null
  } | null
  canDownload: boolean
}
